/**
 * mobile_form_shell_store — coordination registry for the unified mobile form
 * shell (UPT-1616 batch-3, Olivier 2026-08-13).
 *
 * Three concerns, one tiny store (no external dep, useSyncExternalStore-ready):
 *
 *  1. formActions — the currently-mounted form's action targets. FormMenu
 *     registers them so the GLOBAL Geode Copilot drawer (single launcher on
 *     every page) can execute natural-language commands ("save", "annuler"…)
 *     against the form on screen.
 *
 *  2. toolbarController — the currently-mounted legacy Toolbar's mobile sheet
 *     opener. When a bottom action bar is present, the toolbar does NOT render
 *     its own floating trigger; the action bar hosts the "Actions" button
 *     instead (single bottom menu, Olivier's ask).
 *
 *  3. actionBarPresent — true while a mobile FormMenu action bar is mounted,
 *     so the Toolbar knows whether to render its standalone trigger.
 *
 * All registrations return an unregister function (useEffect cleanup).
 * Last-registration-wins; a stack is unnecessary because only one form is
 * visible at a time in the mobile shell.
 */

import type { CommandTarget } from './mobileCommandMatcher';

export interface FormActionsRegistration {
  targets: ReadonlyArray<CommandTarget>;
  /** Execute the action whose map key is `key`. */
  execute: (key: string) => void;
}

export interface ToolbarControllerRegistration {
  open: () => void;
}

type Listener = () => void;

let formActions: FormActionsRegistration | null = null;
let toolbarController: ToolbarControllerRegistration | null = null;
let actionBarPresent = false;

const listeners = new Set<Listener>();

const notify = () => {
  listeners.forEach((listener) => listener());
};

export const subscribeMobileFormShell = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getFormActions = (): FormActionsRegistration | null => formActions;
export const getToolbarController = (): ToolbarControllerRegistration | null => toolbarController;
export const isActionBarPresent = (): boolean => actionBarPresent;

export const registerFormActions = (registration: FormActionsRegistration): (() => void) => {
  formActions = registration;
  notify();
  return () => {
    if (formActions === registration) {
      formActions = null;
      notify();
    }
  };
};

export const registerToolbarController = (registration: ToolbarControllerRegistration): (() => void) => {
  toolbarController = registration;
  notify();
  return () => {
    if (toolbarController === registration) {
      toolbarController = null;
      notify();
    }
  };
};

export const registerActionBar = (): (() => void) => {
  actionBarPresent = true;
  notify();
  return () => {
    actionBarPresent = false;
    notify();
  };
};
